import React from 'react';
import { useTranslation } from 'react-i18next';

interface SimpleScoreBlockProps {
    teamAScore: number;
    teamBScore: number;
    teamAAlignments: number;
    teamBAlignments: number;
    currentRound: number;
    maxRounds?: number;
    financeMode?: boolean;
}

const SimpleScoreBlock: React.FC<SimpleScoreBlockProps> = ({
    teamAScore,
    teamBScore,
    teamAAlignments,
    teamBAlignments,
    currentRound,
    maxRounds = 12,
    financeMode = false
}) => {
    const { t } = useTranslation();
    const totalScore = teamAScore + teamBScore || 1;
    // In finance mode, progress bar might be better as relative to a target or just normalized?
    // For now keep relative to total "wealth"
    const teamAPercentage = (teamAScore / totalScore) * 100;
    const teamBPercentage = (teamBScore / totalScore) * 100;
    const scoreDiff = Math.abs(teamAScore - teamBScore);
    const teamAMultiplier = 1 + (teamAAlignments * 0.5);
    const teamBMultiplier = 1 + (teamBAlignments * 0.5);

    return (
        <div className="flex flex-col gap-2">
            {/* Scores Block */}
            <div className="bg-white dark:bg-slate-800/90 rounded-lg p-3 border border-gray-200 dark:border-slate-400/20 shadow-lg transition-colors duration-300">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white m-0 flex items-center gap-2">
                        <span>{financeMode ? '💰' : '📊'}</span>
                        {financeMode ? t('scores.budget') : t('scores.title').toUpperCase()}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                        {t('round')} {currentRound}/{maxRounds}
                    </span>
                </div>

                {/* Team A */}
                <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                            <span className="text-sm">🔵</span>
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">
                                {t('teamA')}
                            </span>
                            {teamAScore > teamBScore && (
                                <span className="text-sm">👑</span>
                            )}
                        </div>
                        <span className="text-xl font-extrabold text-blue-500 dark:text-blue-400">
                            {Math.round(teamAScore)}
                        </span>
                    </div>

                    {/* Blue Progress Bar */}
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-slate-600/30 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500 ease-out"
                            style={{ width: `${teamAPercentage}%` }}
                        />
                    </div>
                </div>

                {/* Team B */}
                <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                            <span className="text-sm">🔴</span>
                            <span className="text-xs font-semibold text-red-600 dark:text-red-300">
                                {t('teamB')}
                            </span>
                            {teamBScore > teamAScore && (
                                <span className="text-sm">👑</span>
                            )}
                        </div>
                        <span className="text-xl font-extrabold text-red-500 dark:text-red-400">
                            {Math.round(teamBScore)}
                        </span>
                    </div>

                    {/* Red Progress Bar */}
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-slate-600/30 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-400 transition-all duration-500 ease-out"
                            style={{ width: `${teamBPercentage}%` }}
                        />
                    </div>
                </div>

                {/* Gap Display */}
                {scoreDiff > 0 && (
                    <div className="text-center p-1 bg-blue-50 dark:bg-blue-500/10 rounded text-[10px] font-medium text-blue-600 dark:text-blue-400">
                        {t('scores.gap')}: {scoreDiff} {financeMode ? t('scores.credits') : t('scores.points')}
                    </div>
                )}
            </div>

            {/* Alignments Block */}
            <div className="bg-white dark:bg-slate-800/90 rounded-lg p-3 border border-gray-200 dark:border-slate-400/20 shadow-lg transition-colors duration-300">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white m-0 mb-3 flex items-center gap-2">
                    <span>🎯</span>
                    {t('scores.alignments').toUpperCase()}
                </h3>

                <div className="flex justify-between gap-2">
                    {/* Team A Alignments */}
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm">🔵</span>
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">
                                A:
                            </span>
                            <span className="text-lg font-extrabold text-emerald-500 dark:text-emerald-400">
                                {teamAAlignments}
                            </span>
                        </div>
                        {teamAAlignments > 0 && (
                            <div className="text-[10px] text-gray-500 dark:text-slate-400 ml-6">
                                (×{teamAMultiplier.toFixed(1)})
                            </div>
                        )}
                    </div>

                    {/* Team B Alignments */}
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm">🔴</span>
                            <span className="text-xs font-semibold text-red-600 dark:text-red-300">
                                B:
                            </span>
                            <span className="text-lg font-extrabold text-emerald-500 dark:text-emerald-400">
                                {teamBAlignments}
                            </span>
                        </div>
                        {teamBAlignments > 0 && (
                            <div className="text-[10px] text-gray-500 dark:text-slate-400 ml-6">
                                (×{teamBMultiplier.toFixed(1)})
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleScoreBlock;
